import { useState } from "react";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IApiResponse, IHome } from "@miplatform/types";
import { createUUID } from "@miplatform/utils";

const getHomeUUID = createUUID();
const updateHomeUUID = createUUID();
const deleteHomeUUID = createUUID();

export const useHome = () => {
  const stateManager = useComponentState();
  const isLoading = stateManager.isBusy();

  const [home, setHome] = useState<IHome | null>(null);

  const getHome = (id: string): Promise<IApiResponse<IHome>> => {
    stateManager.abort(getHomeUUID);

    return stateManager.run(async () => {
      const res = await homeService.homes.getById({ id });
      setHome(res.data);
      return res;
    }, getHomeUUID);
  };

  const updateHome = ({
    id,
    data,
  }: {
    id: string;
    data: IHome;
  }): Promise<IApiResponse<IHome>> => {
    stateManager.abort(updateHomeUUID);
    return stateManager.run(async () => {
      const response = await homeService.homes.update({ id, data });
      if (response.status === HTTP_STATUSES.OK) {
        setHome(response.data);
      }
      return response;
    }, updateHomeUUID);
  };

  const deleteHome = ({
    homeId,
    modifiedOn,
  }: {
    homeId: string;
    modifiedOn: string;
  }): Promise<IApiResponse> => {
    stateManager.abort(deleteHomeUUID);
    return stateManager.run(async () => {
      return homeService.homes.delete({
        id: homeId,
        modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Home deleted successfully.",
          },
        },
      });
    }, deleteHomeUUID);
  };

  return {
    isLoading,
    home,
    stateManager,
    getHome,
    setHome,
    updateHome,
    deleteHome,
  };
};
